import React from "react";
import PropTypes from "prop-types";
import {
    SimpleForm,
    Create,
    TextInput,
    BooleanInput,
    Notification,
    useNotify,
    NumberInput,
    SaveButton,
    Toolbar,
    SimpleFormIterator,
    FormDataConsumer
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import { ThemeProvider } from "@material-ui/styles";
import customTheme from "../../customTheme";
import { validateMagicNUmber, validateRequired, valueBetween } from "../../utils/Validations";
import CustomAdminSelect from "./CustomAdminSelect";
import VideoPreview from "./VideoPreview";
import  { arrayInputStyles } from "../Stores/StoreEdit";
import { isEmptyValue, normalizeActiveValue, parseMagicNumberInput, transformBestsellerSave, CopySimilarRingsButton } from "./edit-bestseller";

const useStyles = makeStyles(() => ({
    root: {
        width: "90%",
        margin: "auto",
    },
    grouping: {
        border: "1px solid lightgray",
        borderRadius: "3px",
        margin: "1% auto",
    },
    warnLabel: {
        fontSize: "10px",
        color: "red",
        display: "block",
    },
    displayFlex: {
        display: "flex",
    },
    equalWidth: {
        maxWidth: "33%",
    },
    heading: {
        fontWeight: "bold",
        marginTop: "10px",
        borderBottom: "3px solid #a6a5a5ff",
        paddingBottom: "5px",
    },
    copySimilarRingsButtonWrap: {
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
    },
    builderCopyButtonBar: {
        position: "relative",
        minHeight: 40,
        marginBottom: 8,
    },
}));

const CustomToolbar = (props) => {
    const { getState } = useForm();
    const formState = getState();

    return (
        <Toolbar {...props}>
            <SaveButton
                label="Save"
                icon={<></>}
                disabled={formState.invalid}
            />
        </Toolbar>
    );
};

const getValueByPath = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);
export const CustomArrayIterator = ({
    arraySource,
    fieldLabel,
    maxItems = 4,
}) => {
    const arrayClasses = arrayInputStyles();
    const { values } = useFormState();
    const currentLength = getValueByPath(values, arraySource)?.length || 0;

    return (
        <SimpleFormIterator
            inline
            classes= {{
                root: arrayClasses.root,
                form: arrayClasses.form,
                indexContainer: arrayClasses.indexContainer,
                action: arrayClasses.action,
            }}
            disableAdd={currentLength >= maxItems}
        >
            <TextInput
                label={fieldLabel}
                validate={validateMagicNUmber}
                fullWidth
            />
        </SimpleFormIterator>
    );
};

CustomArrayIterator.propTypes = {
    arraySource: PropTypes.string,
    fieldLabel: PropTypes.string,
    maxItems: PropTypes.number,
};

const CreateBestSeller = (props) => {
    const classes = useStyles();

    const notify = useNotify();
    const onFailure = (error) => {
        const errors = error.response?.data?.errors;

        if (errors) {
            const messages = [];
            for (const key in errors) {
                if (errors.hasOwnProperty(key)) {
                    const errorMessages = errors[key];
                    if (Array.isArray(errorMessages)) {
                        messages.push(...errorMessages);
                    } else {
                        messages.push(errorMessages);
                    }
                }
            }
            console.log(messages);
            notify(messages[0], { type: "error" });
        } else {
            notify(
                error.response.data.message
                    ? error.response.data.message
                    : "Something went wrong",
                { type: "warning" }
            );
        }
    };

    return (
        <ThemeProvider theme={customTheme}>
            <Create onFailure={onFailure} {...props}>
                <SimpleForm
                    redirect="list"
                    toolbar={<CustomToolbar />}
                    sanitizeEmptyValues={false}
                    transform={transformBestsellerSave}
                >
                    <Grid
                        container
                        spacing={2}
                        className={classes.root}
                        justify="space-between"
                    >
                        <Grid
                            item
                            container
                            xs={12}
                            className={classes.displayFlex}
                            alignItems="center"
                            justify="space-between"
                        >
                            <Grid item xs={4}>
                                <TextInput
                                    source="magic_number"
                                    label="Magic Number"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <NumberInput
                                    source="discount_percentage"
                                    label="Discount Percentage"
                                    validate={[
                                        validateRequired,
                                        valueBetween(0, 100),
                                    ]}
                                    fullWidth
                                />
                            </Grid>
                            <FormDataConsumer>
                                {({ formData }) => {
                                    const isActive =
                                        formData?.active === undefined
                                            ? true
                                            : Boolean(formData.active);

                                    return (
                                        <Grid item xs={2}>
                                            <BooleanInput
                                                label={isActive ? "Active" : "Inactive"}
                                                source="active"
                                                initialValue={1}
                                                parse={(v) => (v ? 1 : 0)}
                                            />
                                        </Grid>
                                    );
                                }}
                            </FormDataConsumer>
                            <Grid item xs={4}>
                                <CustomAdminSelect
                                    label="Video URL"
                                    source="video_url"
                                    resource="getBestsellerVideoList"
                                />
                            </Grid>
                            <Grid
                                item
                                container
                                xs={12}
                                spacing={2}
                                alignItems="center"
                            >
                                <Grid item xs={4}>
                                    <BooleanInput
                                        label="Hide Quarter Sizes"
                                        source="hide_quarter_sizes"
                                        initialValue={false}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <BooleanInput
                                        label="Hide Outside Features In Highlights"
                                        source="hide_outside_features_in_highlights"
                                        initialValue={false}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <BooleanInput
                                        label="Hide Inside Features In Highlights"
                                        source="hide_inside_features_in_highlights"
                                        initialValue={false}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <VideoPreview />
                        <Grid item xs={5}>
                            {[1, 2, 3, 4, 5].map((ringPosition) => (
                                <TextInput
                                    key={`similar-ring-${ringPosition}`}
                                    source={`similar_rings[${ringPosition - 1}]`}
                                    label={`Similar Ring ${ringPosition}`}
                                    validate={validateMagicNUmber}
                                    parse={parseMagicNumberInput}
                                    fullWidth
                                />
                            ))}
                        </Grid>
                    </Grid>
                    {/* <Grid
                        container
                        spacing={2}
                        className={classes.root}
                        alignItems="center"
                        justify="space-between"
                    >
                        <Grid
                            item
                            container
                            xs={12}
                            className={classes.heading}
                        >
                            Kay's Configuration
                        </Grid>
                        <Grid item xs={3}>
                            <NumberInput
                                source="kays_discount_percentage"
                                label="Kay's Discount Percentage"
                                validate={[
                                    validateRequired,
                                    valueBetween(0, 100),
                                ]}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <BooleanInput
                                label={kaysChecked ? "Active For Kays" : "InActive For Kays"}
                                source="active_for_kays"
                                initialValue={1}
                                onChange={handleKaysChange}
                                checked={kaysChecked}
                                parse={(v) => (v ? 1 : 0)}
                            />
                        </Grid>
                    </Grid> */}
                    <Grid
                        container
                        spacing={2}
                        className={classes.root}
                        alignItems="center"
                        justify="space-between"
                    >
                        <Grid
                            item
                            container
                            xs={12}
                            className={classes.heading}
                        >
                            Builder Platform Configuration
                        </Grid>
                        <FormDataConsumer>
                            {({ formData }) => {
                                const builderPlatformId =
                                    formData?.builder_platform_data?.builder_platform_id;
                                const showCopyButton = !isEmptyValue(builderPlatformId);

                                return showCopyButton ? (
                                    <Grid item xs={12} className={classes.builderCopyButtonBar}>
                                        <div className={classes.copySimilarRingsButtonWrap}>
                                            <CopySimilarRingsButton />
                                        </div>
                                    </Grid>
                                ) : null;
                            }}
                        </FormDataConsumer>
                        <Grid item xs={3}>
                            <CustomAdminSelect
                                label="Builder Platform"
                                source="builder_platform_data.builder_platform_id"
                                resource="builder-platform?page[number]=1&page[size]=1000"
                                optionLabel="store_name"
                            />
                        </Grid>
                        <FormDataConsumer>
                            {({ formData }) => {
                                const { builder_platform_id, id, active } = formData?.builder_platform_data || {};
                                const isBuilderActive = normalizeActiveValue(active, false);
                                const showBuilderFields = !isEmptyValue(builder_platform_id);

                                return showBuilderFields ? (
                                    <>
                                        <Grid item xs={2}>
                                            <BooleanInput
                                                label={isBuilderActive ? "Active" : "Inactive"}
                                                source="builder_platform_data.active"
                                                format={(value) => normalizeActiveValue(value, false)}
                                                parse={(v) => (v ? 1 : 0)}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <NumberInput
                                                source="builder_platform_data.discount_percentage"
                                                label="Builder Discount Percentage"
                                                validate={isBuilderActive ?[
                                                    validateRequired,
                                                    valueBetween(0, 100),
                                                ] : [valueBetween(0, 100)]}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            {[1, 2, 3, 4, 5].map((ringPosition) => (
                                                <TextInput
                                                    key={`builder-ring-${ringPosition}`}
                                                    source={`builder_platform_data.similar_rings[${ringPosition - 1}]`}
                                                    label={`Similar Ring ${ringPosition}`}
                                                    fullWidth
                                                    validate={validateMagicNUmber}
                                                    parse={parseMagicNumberInput}
                                                />
                                            ))}
                                        </Grid>
                                    </>
                                ) : null;
                            }}
                        </FormDataConsumer>
                    </Grid>
                </SimpleForm>
            </Create>
            <Notification />
        </ThemeProvider>
    );
};

export default CreateBestSeller;
